'use client';

import { useMemo, useState } from 'react';
import {
  buildCron,
  convertCssPxToRem,
  CRON_PRESETS,
  cssToTailwind,
  explainCron,
  jsonToTypeScript,
  parseRoot,
  pxToRem,
  remToPx,
  type CronFields,
  type DevConverterVariant,
} from '@/lib/browser-devconvert-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const HEADINGS: Record<DevConverterVariant, { badge: string; title: string; subtitle: string }> = {
  'px-to-rem-converter': {
    badge: 'px ↔ rem',
    title: 'PX to REM converter',
    subtitle:
      'Convert pixels to rem and back with an adjustable root font-size, or batch-convert a whole CSS block. Everything runs in your browser.',
  },
  'json-to-typescript': {
    badge: 'JSON → TS',
    title: 'JSON to TypeScript',
    subtitle:
      'Paste JSON and get TypeScript interfaces with inferred nested objects, arrays, optional and nullable fields. Nothing is uploaded.',
  },
  'css-to-tailwind': {
    badge: 'CSS → Tailwind',
    title: 'CSS to Tailwind',
    subtitle:
      'Paste plain CSS declarations and get approximate Tailwind utility classes. Unmapped properties are listed honestly. Runs locally.',
  },
  'cron-builder': {
    badge: 'cron',
    title: 'Cron expression builder',
    subtitle:
      'Build a cron expression from fields and presets, or paste one to read a plain-English explanation. Everything runs in your browser.',
  },
};

function Shell({ variant, children }: { variant: DevConverterVariant; children: React.ReactNode }) {
  const head = HEADINGS[variant];
  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{head.badge}</span>
        <h2 className="tool-live-title">{head.title}</h2>
        <p className="tool-live-subtitle">{head.subtitle}</p>
      </div>
      <div className="tool-live-grid">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function PxToRemView() {
  const [rootValue, setRootValue] = useState('16');
  const [pxValue, setPxValue] = useState('16');
  const [remValue, setRemValue] = useState('1');
  const [bulk, setBulk] = useState('');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const root = parseRoot(rootValue);
  const bulkOutput = useMemo(() => convertCssPxToRem(bulk, root), [bulk, root]);

  async function handleCopy(value: string) {
    if (!value) return;
    try {
      await copyText(value);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <>
      <section className="tool-live-uploader">
        <div className="tool-live-controls">
          <label className="tool-live-field">
            <span>Root font-size (px)</span>
            <input
              inputMode="decimal"
              onChange={(event) => {
                setCopyMessage(null);
                setRootValue(event.target.value);
              }}
              placeholder="16"
              type="text"
              value={rootValue}
            />
          </label>
          <label className="tool-live-field">
            <span>Pixels</span>
            <input
              inputMode="decimal"
              onChange={(event) => {
                setCopyMessage(null);
                setPxValue(event.target.value);
                setRemValue(pxToRem(event.target.value, root));
              }}
              placeholder="16"
              type="text"
              value={pxValue}
            />
          </label>
          <label className="tool-live-field">
            <span>Rem</span>
            <input
              inputMode="decimal"
              onChange={(event) => {
                setCopyMessage(null);
                setRemValue(event.target.value);
                setPxValue(remToPx(event.target.value, root));
              }}
              placeholder="1"
              type="text"
              value={remValue}
            />
          </label>
        </div>
        <div className="tool-live-actions">
          <button
            className="button button-secondary"
            onClick={() => handleCopy(pxToRem(pxValue, root))}
            type="button"
          >
            Copy rem
          </button>
          <button
            className="button button-secondary"
            onClick={() => handleCopy(remToPx(remValue, root))}
            type="button"
          >
            Copy px
          </button>
        </div>
        {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
      </section>

      <section className="tool-live-preview">
        <label className="tool-live-field tool-live-field-full">
          <span>Bulk: paste CSS with px values</span>
          <textarea
            onChange={(event) => {
              setCopyMessage(null);
              setBulk(event.target.value);
            }}
            placeholder={'padding: 16px 24px;\nfont-size: 14px;\nborder-radius: 8px;'}
            rows={6}
            value={bulk}
          />
        </label>
        <label className="tool-live-field tool-live-field-full">
          <span>Converted to rem</span>
          <textarea readOnly rows={6} value={bulkOutput} />
        </label>
        <div className="tool-live-actions">
          {bulkOutput ? (
            <button
              className="button button-secondary"
              onClick={() => handleCopy(bulkOutput)}
              type="button"
            >
              Copy CSS
            </button>
          ) : null}
        </div>
        <p className="tool-live-panel-note">
          Every px value is divided by the root font-size. Runs locally in your browser.
        </p>
      </section>
    </>
  );
}

/* ------------------------------------------------------------------ */

function JsonToTypeScriptView() {
  const [input, setInput] = useState('{\n  "id": 1,\n  "name": "Ada",\n  "tags": ["dev"]\n}');
  const [rootName, setRootName] = useState('Root');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const { output, error } = useMemo(() => jsonToTypeScript(input, rootName), [input, rootName]);

  async function handleCopy() {
    if (!output) return;
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <>
      <section className="tool-live-uploader">
        <label className="tool-live-field">
          <span>Root interface name</span>
          <input
            onChange={(event) => {
              setCopyMessage(null);
              setRootName(event.target.value);
            }}
            placeholder="Root"
            type="text"
            value={rootName}
          />
        </label>
        <label className="tool-live-field tool-live-field-full">
          <span>JSON input</span>
          <textarea
            onChange={(event) => {
              setCopyMessage(null);
              setInput(event.target.value);
            }}
            rows={12}
            value={input}
          />
        </label>
        {error ? (
          <p className="status-message" style={{ color: '#dc2626' }}>
            {error}
          </p>
        ) : null}
        {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
      </section>

      <section className="tool-live-preview">
        <label className="tool-live-field tool-live-field-full">
          <span>TypeScript output</span>
          <textarea readOnly rows={14} value={output} />
        </label>
        <div className="tool-live-actions">
          {output ? (
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy TypeScript
            </button>
          ) : null}
        </div>
        <p className="tool-live-panel-note">
          Types are inferred from your sample. Runs locally in your browser.
        </p>
      </section>
    </>
  );
}

/* ------------------------------------------------------------------ */

function CssToTailwindView() {
  const [input, setInput] = useState(
    'display: flex;\njustify-content: center;\nalign-items: center;\npadding: 16px;\nfont-size: 14px;\nborder-radius: 8px;',
  );
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const { classes, unmapped } = useMemo(() => cssToTailwind(input), [input]);

  async function handleCopy() {
    if (!classes) return;
    try {
      await copyText(classes);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <>
      <section className="tool-live-uploader">
        <label className="tool-live-field tool-live-field-full">
          <span>CSS declarations</span>
          <textarea
            onChange={(event) => {
              setCopyMessage(null);
              setInput(event.target.value);
            }}
            placeholder={'margin: 24px;\ncolor: #1a1a1a;\ndisplay: flex;'}
            rows={12}
            value={input}
          />
        </label>
        <div className="tool-live-actions">
          {classes ? (
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy classes
            </button>
          ) : null}
        </div>
        {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
      </section>

      <section className="tool-live-preview">
        <label className="tool-live-field tool-live-field-full">
          <span>Tailwind classes</span>
          <textarea readOnly rows={5} value={classes} />
        </label>
        {unmapped.length > 0 ? (
          <label className="tool-live-field tool-live-field-full">
            <span>Unmapped properties (no direct utility)</span>
            <textarea readOnly rows={4} value={unmapped.join('\n')} />
          </label>
        ) : null}
        <p className="tool-live-panel-note">
          Mappings are approximate and assume a default Tailwind config. Runs locally in your
          browser.
        </p>
      </section>
    </>
  );
}

/* ------------------------------------------------------------------ */

function CronBuilderView() {
  const [fields, setFields] = useState<CronFields>({
    minute: '0',
    hour: '9',
    dayOfMonth: '*',
    month: '*',
    dayOfWeek: '1',
  });
  const [expression, setExpression] = useState('0 9 * * 1');
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const built = useMemo(() => buildCron(fields), [fields]);
  const explained = useMemo(() => explainCron(expression), [expression]);

  function setField(key: keyof CronFields, value: string) {
    setCopyMessage(null);
    setFields((prev) => {
      const next = { ...prev, [key]: value };
      setExpression(buildCron(next));
      return next;
    });
  }

  async function handleCopy() {
    try {
      await copyText(expression || built);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  const fieldDefs: { key: keyof CronFields; label: string; placeholder: string }[] = [
    { key: 'minute', label: 'Minute (0-59)', placeholder: '0' },
    { key: 'hour', label: 'Hour (0-23)', placeholder: '9' },
    { key: 'dayOfMonth', label: 'Day of month (1-31)', placeholder: '*' },
    { key: 'month', label: 'Month (1-12)', placeholder: '*' },
    { key: 'dayOfWeek', label: 'Day of week (0-6)', placeholder: '*' },
  ];

  return (
    <>
      <section className="tool-live-uploader">
        <div className="tool-live-controls">
          <label className="tool-live-field tool-live-field-full">
            <span>Presets</span>
            <select
              onChange={(event) => {
                const preset = CRON_PRESETS.find((item) => item.label === event.target.value);
                if (preset) {
                  setCopyMessage(null);
                  setFields(preset.value);
                  setExpression(buildCron(preset.value));
                }
              }}
              value=""
            >
              <option value="">Choose a preset…</option>
              {CRON_PRESETS.map((preset) => (
                <option key={preset.label} value={preset.label}>
                  {preset.label}
                </option>
              ))}
            </select>
          </label>
          {fieldDefs.map((def) => (
            <label className="tool-live-field" key={def.key}>
              <span>{def.label}</span>
              <input
                onChange={(event) => setField(def.key, event.target.value)}
                placeholder={def.placeholder}
                type="text"
                value={fields[def.key]}
              />
            </label>
          ))}
        </div>
      </section>

      <section className="tool-live-preview">
        <label className="tool-live-field tool-live-field-full">
          <span>Cron expression</span>
          <input
            onChange={(event) => {
              setCopyMessage(null);
              setExpression(event.target.value);
            }}
            placeholder="0 9 * * 1"
            type="text"
            value={expression}
          />
        </label>
        {explained.error ? (
          <p className="status-message" style={{ color: '#dc2626' }}>
            {explained.error}
          </p>
        ) : (
          <label className="tool-live-field tool-live-field-full">
            <span>Plain-English summary</span>
            <textarea readOnly rows={3} value={explained.summary} />
          </label>
        )}
        <div className="tool-live-actions">
          <button className="button button-secondary" onClick={handleCopy} type="button">
            Copy expression
          </button>
        </div>
        {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        <p className="tool-live-panel-note">
          Standard 5-field cron (minute hour day-of-month month day-of-week). Runs locally in your
          browser.
        </p>
      </section>
    </>
  );
}

/* ------------------------------------------------------------------ */

export default function BrowserDevConverter({ variant }: { variant: DevConverterVariant }) {
  return (
    <Shell variant={variant}>
      {variant === 'px-to-rem-converter' ? <PxToRemView /> : null}
      {variant === 'json-to-typescript' ? <JsonToTypeScriptView /> : null}
      {variant === 'css-to-tailwind' ? <CssToTailwindView /> : null}
      {variant === 'cron-builder' ? <CronBuilderView /> : null}
    </Shell>
  );
}
